import React, { useMemo } from 'react';
import pathOrDash from 'App/utils/pathOrDash';
import { useFormikContext } from 'formik';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { authorizationList } from '../authorizationList';
import Table from 'App/components/Table/Table';
import SelectField from 'Shared/form/SelectField';
import { TextField } from 'Shared/form/TextField';
import TherigyInfoMessage from 'App/components/TherigyInfoMessage/TherigyInfoMessage';
import CommonPatientFieldWrapper from 'App/components/PatientForm/components/CommonPatientFieldWrapper';
import RequiredTableHeaderLabel from 'Shared/form/RequiredTableHeaderLabel';

const ndcSpecificApprovalList = [
    {
        id: '1',
        name: 'Yes',
    },
    {
        id: '2',
        name: 'No',
    },
];

const cellPropTypes = {
    row: PropTypes.shape({
        id: PropTypes.string.isRequired,
    }).isRequired,
};

const generateAuthorizerQuantityCell = (disableActivity, disableInputs) => {
    const AuthorizerQuantityCell = ({ row }) => (
        <CommonPatientFieldWrapper fieldName="referralPaAuthorizedQuantity">
            {(commonProps) => (
                <TextField
                    {...commonProps}
                    fieldName={`medications[${row.id}].data.authorizedQuantity`}
                    disabled={commonProps.disabled || disableActivity || disableInputs}
                    placeholder="Authorized Quantity"
                />
            )}
        </CommonPatientFieldWrapper>
    );

    AuthorizerQuantityCell.propTypes = cellPropTypes;

    return AuthorizerQuantityCell;
};

const generateAuthorizerUomCell = (disableActivity, disableInputs) => {
    const AuthorizerUomCell = ({ row }) => (
        <CommonPatientFieldWrapper fieldName="referralPaUnitOfMeasure">
            {(commonProps) => (
                <SelectField
                    {...commonProps}
                    isClearable
                    options={authorizationList.map((it) => ({ id: it, name: it }))}
                    fieldName={`medications[${row.id}].data.authorizedUom`}
                    isDisabled={commonProps.disabled || disableActivity || disableInputs}
                    getOptionValue={R.prop('id')}
                    getOptionLabel={R.prop('name')}
                />
            )}
        </CommonPatientFieldWrapper>
    );

    AuthorizerUomCell.propTypes = cellPropTypes;

    return AuthorizerUomCell;
};

const generateNdcSpecificApprovalCell = (disableActivity, disableInputs) => {
    const NdcSpecificApprovalCell = ({ row }) => (
        <CommonPatientFieldWrapper fieldName="referralPaIsApprovalNdcSpecific">
            {(commonProps) => (
                <SelectField
                    {...commonProps}
                    options={ndcSpecificApprovalList}
                    isClearable
                    fieldName={`medications[${row.id}].data.ndcSpecificApproval`}
                    isDisabled={commonProps.disabled || disableActivity || disableInputs}
                    getOptionValue={R.prop('id')}
                    getOptionLabel={R.prop('name')}
                />
            )}
        </CommonPatientFieldWrapper>
    );

    NdcSpecificApprovalCell.propTypes = cellPropTypes;

    return NdcSpecificApprovalCell;
};

const generateApproveNdcCell = (disableActivity, disableInputs) => {
    const ApproveNdcCell = ({ row }) => (
        <CommonPatientFieldWrapper fieldName="referralPaApprovedNdc">
            {(commonProps) => (
                <TextField
                    {...commonProps}
                    disabled={commonProps.disabled || disableActivity || disableInputs}
                    fieldName={`medications[${row.id}].data.approvedNdc`}
                    placeholder="Approved NDC"
                />
            )}
        </CommonPatientFieldWrapper>
    );

    ApproveNdcCell.propTypes = cellPropTypes;

    return ApproveNdcCell;
};

const MedicationTableForm = () => {
    const { values } = useFormikContext();
    const { disableActivity, disableInputs } = values;

    const columns = useMemo(
        () => [
            {
                Header: 'Medication',
                accessor: 'medication.name',
                Cell: ({ row }) => pathOrDash(['original', 'medication', 'name'], row),
                id: 'medication',
                disableFilters: true,
                canSort: false,
            },
            {
                Header: (
                    <RequiredTableHeaderLabel label="Authorized Quantity" fieldName="referralPaAuthorizedQuantity" />
                ),
                accessor: 'data.authorizedQuantity',
                Cell: generateAuthorizerQuantityCell(disableActivity, disableInputs),
                id: 'authorizedQuantity',
                disableFilters: true,
                canSort: false,
            },
            {
                Header: <RequiredTableHeaderLabel label="Unit of Measure" fieldName="referralPaUnitOfMeasure" />,
                accessor: 'data.authorizedUom',
                Cell: generateAuthorizerUomCell(disableActivity, disableInputs),
                id: 'authorizedUom',
                disableFilters: true,
                canSort: false,
            },
            {
                Header: (
                    <RequiredTableHeaderLabel
                        label="Is Approval NDC Specific?"
                        fieldName="referralPaIsApprovalNdcSpecific"
                    />
                ),
                accessor: 'data.ndcSpecificApproval',
                Cell: generateNdcSpecificApprovalCell(disableActivity, disableInputs),
                id: 'ndcSpecificApproval',
                disableFilters: true,
                canSort: false,
            },
            {
                Header: <RequiredTableHeaderLabel label="Approved NDC" fieldName="referralPaApprovedNdc" />,
                accessor: 'data.approvedNdc',
                Cell: generateApproveNdcCell(disableActivity, disableInputs),
                id: 'approvedNdc',
                disableFilters: true,
                canSort: false,
            },
        ],
        [disableActivity, disableInputs]
    );

    if (values?.medications && values?.medications.length === 0) {
        return (
            <TherigyInfoMessage message="There is currently no medication attached to this Prior Authorization Activity." />
        );
    }

    return (
        <>
            <h3>Medication</h3>
            <Table enablePagination={false} columns={columns} data={values?.medications || []} canSort={false} />
        </>
    );
};

export default MedicationTableForm;
